import { defineStore } from 'pinia'
import TextCell from '@/onenetwork/components/datatable/TextCell.vue'
import MoneyCell from '@/onenetwork/components/datatable/MoneyCell.vue'
import DateTimeCell from '@/onenetwork/components/datatable/DateTimeCell.vue'
import { uuid } from 'vue3-uuid'
import { APIClient } from '@/onenetwork/core/onenetwork.api.instant.ts'
import TransactionAPI from '../constants/transaction.api.uri.const'
import { formatTableDataTransaction } from '../helper/table'
import { buildFilterObject } from '@/utils/filter'
import PhoneCell from '@/onenetwork/components/datatable/PhoneCell.vue'
import ColorCell from '@/onenetwork/components/datatable/ColorCell.vue'
import TransactionListCustomActionCell from '@/modules/transaction/components/table/TransactionListCustomActionCell.vue'
import {markRaw} from 'vue'


export enum TransactionListActionTypes {
  FILTERING = 'FILTERING',
  PENDING = 'PENDING',
  FETCHING = 'FETCHING',
  FETCH_SUCCESS = 'FETCH_SUCCESS',
  FETCH_FAILURE = 'FETCH_FAILURE',
}

interface TransactionListStore {
  action_type: TransactionListActionTypes
  action_mess: string
  uuid: string
  data: Object
  filter: Object
  pageSize: number
  page: number
  tableConfig: Object
  selectedIds: Array<string | number>
}

export const useTransactionStore = defineStore('transaction.list', {
  state: (): TransactionListStore => ({
    action_type: TransactionListActionTypes.PENDING,
    action_mess: '',
    uuid: '',
    data: {
      list: [],
      totalRecords: 0,
      totalPage: 0,
    },
    filter: [
      {
        key: 'code',
        type: 'text',
        label: 'Mã tài khoản',
        placeholder: 'Nhập mã tài khoản',
        value: '',
      },
      {
        key: 'transaction_code',
        type: 'text',
        label: 'Mã GD',
        placeholder: 'Nhập mã GD',
        value: '',
      },
      {
        key: 'keyword',
        type: 'taginput',
        label: 'Lọc theo từ khoá',
        placeholder: 'Nhập từ khoá',
        value: '',
      },
      {
        key: 'balance_type',
        type: 'select-multiple',
        label: 'Lọc theo loại ví',
        placeholder: 'Chọn ví',
        value: '',
        options: [
          { label: 'Tiền mặt', value: 'cash' },
          { label: 'Điểm', value: 'point' },
        ],
      },
      {
        key: 'type',
        type: 'select-multiple',
        label: 'Loại GD',
        placeholder: 'Chọn loại GD',
        value: '',
        options: [
          { label: 'Nạp tiền', value: 'deposit' },
          { label: 'Rút tiền', value: 'withdrawal' },
          { label: 'Sử dụng', value: 'spend' },
          { label: 'Thưởng', value: 'reward' },
        ],
      },
      {
        key: 'transaction_date',
        type: 'date-range',
        label: 'Lọc theo ngày xử lý',
        placeholder: 'Chọn ngày xử lý',
        value: '',
      },
    ],
    pageSize: 10,
    page: 1,
    tableConfig: {
      canShowHide: true,
      columns: {
        created_at: {
          label: 'Thời gian',
          component: DateTimeCell,
          visible: true,
          sort: 'desc',
          config: {},
        },
        balance_type: { label: 'Ví', component: markRaw(TextCell), visible: true, sort: false, config: {} },
        transaction_type: { label: 'Loại GD', component: markRaw(ColorCell), visible: true, sort: false, config: {} },
        amount: { label: 'Số tiền', component: markRaw(MoneyCell), class: 'justify-end', visible: true, sort: false, config: {} },
        description: { label: 'Nội dung', component: markRaw(TextCell), visible: true, sort: false, config: {} },
        transaction_code: {
          label: 'Mã giao dịch',
          component: TextCell,
          visible: true,
          sort: false,
          config: {},
        },
        account_code: {
          label: 'Mã tài khoản',
          component: TextCell,
          visible: true,
          sort: false,
          config: {},
        },
        phone: { label: 'SDT', component: markRaw(PhoneCell), visible: true, sort: false, config: {} },
        full_name: { label: 'Tên', component: markRaw(TextCell), visible: true, sort: false, config: {} },
        action_by: {
          label: 'Người xử lý',
          component: TextCell,
          visible: true,
          sort: false,
          config: {},
        },
      },
    },
    selectedIds: [],
  }),
  getters: {
    sortFilter: (state) => {
      const sort: any = []
      if (Object.keys(state.tableConfig?.columns).length > 0) {
        Object.keys(state.tableConfig?.columns).forEach((col) => {
          if (state.tableConfig.columns[col].sort) {
            sort.push([col, state.tableConfig.columns[col].sort])
          }
        })
      }
      return sort
    },
  },
  actions: {
    //FILTER ACTION
    setFilter(filter: any) {
      this.filter = filter
      this.page = 1
      this.action_type = TransactionListActionTypes.FILTERING
      this.uuid = uuid.v4()
    },
    putFilter(key: string, value: any) {
      this.filter = this.filter.map((field) => {

        if (field.type === 'select-multiple') {
          if (field.key === key) {
            const newValue = Array.isArray(value) ? value : [value]
            return { ...field, value: newValue }
          }
          return field
        }
        if (field.key === key) {
          return { ...field, value: value }
        }
        return field
      })
      this.page = 1
      this.action_type = TransactionListActionTypes.FILTERING
      this.uuid = uuid.v4()
    },

    resetFilter () {
      this.filter = this.filter.map(item => ({...item, value: ''}))
      this.page = 1
      this.action_type = TransactionListActionTypes.FILTERING
      this.uuid = uuid.v4()
    },

    //PAGE ACTION
    changePage(page: number) {
      this.page = page
      this.action_type = TransactionListActionTypes.FILTERING
      this.uuid = uuid.v4()
    },
    changePageSize(size: number) {
      this.pageSize = size
      this.action_type = TransactionListActionTypes.FILTERING
      this.uuid = uuid.v4()
    },

    //TABLE CONFIG ACTION
    toggleColumnVisibility(columnCode: string, visible: boolean) {
      if (this.tableConfig?.columns[columnCode]) {
        this.tableConfig.columns[columnCode].visible = visible
      } else {
        console.warn(`Column "${columnCode}" không tồn tại trong tableConfig`)
      }
    },
    toggleSort(columnCode: string, sort: string) {
      if (this.tableConfig?.columns[columnCode]) {
        this.tableConfig.columns[columnCode].sort = sort
        this.action_type = TransactionListActionTypes.FILTERING
        this.uuid = uuid.v4()
      } else {
        console.warn(`Column "${columnCode}" không tồn tại trong tableConfig`)
      }
    },

    //DATA ACTION
    async fetchList() {
      return new Promise(async (resolve, reject) => {
        try {
          this.action_type = TransactionListActionTypes.FETCHING
          const sort = this.sortFilter
          const pagination = {
            page: this.page,
            page_size: this.pageSize,
            sort,
          }

          const filter = buildFilterObject(this.filter, pagination)
          const res = await APIClient.get(TransactionAPI.transactions.list, {
            params: filter,
          })
          const { data } = res.data
          this.data = {
            list: formatTableDataTransaction(data.data) || [],
            totalRecords: data.total ?? 0,
            totalPage: data.last_page ?? 0
          }
          this.action_type = TransactionListActionTypes.FETCH_SUCCESS
          resolve(res)
        } catch (e) {
          this.action_type = TransactionListActionTypes.FETCH_FAILURE
          reject(e)
        }
      })
    },
  },
})
